import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import ValidacaoService from "src/services/motor-validacao-service"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"

import {
    buildValidationPayload,
    createInitialValidationParameters,
    mapSavedValidation,
} from "../validation-contract"

const getSaveErrorMessage = (error, fallback) => {
    const body = error.response?.body
    const message = body?.Message ?? body?.erro
    if (typeof message === "string" && message.trim()) return message.trim()
    const fieldErrors = Object.values(body ?? {})
        .filter(Array.isArray).flat().filter((value) => typeof value === "string")
    return fieldErrors.join(" ") || fallback
}

const useValidationData = ({
    cadastroId,
    motorService,
    onEmissionSaved,
    onSnapshotChange,
    snapshotRevision = 0,
}) => {
    const validacaoService = useMemo(() => new ValidacaoService(), [])

    const [parameters, setParameters] = useState(
        createInitialValidationParameters
    )
    const [lookup, setLookup] = useState({
        key: null,
        loading: false,
        viewModel: null,
    })
    const [isSavingEmission, setIsSavingEmission] = useState(false)
    const [referenceDateError, setReferenceDateError] = useState(false)

    const mountedRef = useRef(false)
    const requestVersionRef = useRef(0)
    const previousCadastroIdRef = useRef(cadastroId)
    const dateEditedRef = useRef(false)
    const editedReferencesRef = useRef({})
    const parametersRef = useRef(parameters)
    parametersRef.current = parameters
    const savingEmissionRef = useRef(false)
    const snapshotRevisionRef = useRef(snapshotRevision)
    snapshotRevisionRef.current = snapshotRevision

    const [isSavingValidation, setIsSavingValidation] = useState(false)
    const savingValidationRef = useRef(false)

    const hasCadastroId = cadastroId !== null
        && cadastroId !== undefined
        && cadastroId !== ""

    const dataReferencia = parameters.dataReferencia
    const requestKey = JSON.stringify([cadastroId, dataReferencia])

    const currentLookup = lookup.key === requestKey
        && lookup.snapshotRevision === snapshotRevision
        ? lookup
        : {
            loading: lookup.key !== requestKey && hasCadastroId && Boolean(dataReferencia),
            viewModel: null,
        }

    const updateParameters = useCallback((updater) => {
        const current = parametersRef.current
        const next = typeof updater === "function" ? updater(current) : updater
        if (next.dataReferencia !== current.dataReferencia) dateEditedRef.current = true
        for (const field of ["puparReferencia", "pumtmReferencia", "spreadCredito"]) {
            if (next[field] !== current[field]) editedReferencesRef.current[field] = true
        }
        parametersRef.current = next
        requestVersionRef.current += 1
        setParameters(next)
        onSnapshotChange?.()
    }, [onSnapshotChange])

    useEffect(() => {
        mountedRef.current = true

        return () => {
            mountedRef.current = false
        }
    }, [])

    useEffect(() => {
        let active = true
        const loadDefaultDate = async () => {
            try {
                const response = await validacaoService.getDataReferenciaPadrao()
                const date = response?.body?.Data?.DataReferencia
                if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error("Data padrão inválida")
                if (active && !dateEditedRef.current) {
                    setParameters((current) => ({ ...current, dataReferencia: date }))
                }
            } catch (error) {
                if (active && !dateEditedRef.current) {
                    setReferenceDateError(true)
                    dispatchSnackbar("Não foi possível obter a data padrão. Informe a data de referência manualmente.", "warning")
                }
            }
        }
        loadDefaultDate()
        return () => { active = false }
    }, [validacaoService])

    useEffect(() => {
        if (previousCadastroIdRef.current === cadastroId) return
        previousCadastroIdRef.current = cadastroId
        editedReferencesRef.current = {}
        setParameters((current) => ({
            ...current,
            puparReferencia: "",
            pumtmReferencia: "",
            spreadCredito: "",
        }))
    }, [cadastroId])

    useEffect(() => {
        const requestVersion = ++requestVersionRef.current
        const lookupSnapshotRevision = snapshotRevisionRef.current

        const isCurrent = () => (
            mountedRef.current
            && requestVersionRef.current === requestVersion
            && snapshotRevisionRef.current === lookupSnapshotRevision
        )

        const cleanup = () => {
            if (requestVersionRef.current === requestVersion) {
                requestVersionRef.current += 1
            }
        }

        setLookup({
            key: requestKey,
            snapshotRevision: lookupSnapshotRevision,
            loading: hasCadastroId && Boolean(dataReferencia),
            viewModel: null,
        })

        if (!hasCadastroId || !dataReferencia) {
            return cleanup
        }

        const loadValidation = async () => {
            try {
                const response = await validacaoService.getUltimaValidacao(
                    cadastroId,
                    dataReferencia
                )

                // O ServiceRequest pode retornar undefined após redirecionar.
                if (!isCurrent() || !response) return

                const data = response.body

                // Response(None) também pode chegar como corpo vazio.
                if (!data || Object.keys(data).length === 0) return

                const mapped = mapSavedValidation(data)

                setParameters((current) => {
                    const next = { ...current }
                    for (const field of ["puparReferencia", "pumtmReferencia", "spreadCredito"]) {
                        if (!editedReferencesRef.current[field]) next[field] = mapped.parameters[field]
                    }
                    parametersRef.current = next
                    return next
                })

                setLookup({
                    key: requestKey,
                    snapshotRevision: lookupSnapshotRevision,
                    loading: false,
                    viewModel: mapped.viewModel,
                })
            } catch (error) {
                if (!isCurrent()) return

                dispatchSnackbar(
                    error.response?.body?.Message
                    || "Não foi possível carregar a validação salva.",
                    "error"
                )
            } finally {
                if (isCurrent()) {
                    setLookup((current) => ({
                        ...current,
                        loading: false,
                    }))
                }
            }
        }

        loadValidation()

        return cleanup
    }, [
        cadastroId,
        dataReferencia,
        hasCadastroId,
        requestKey,
        validacaoService,
    ])

    const saveEmission = useCallback(async ({
        spreadEmissao,
        prazoMedioEmissao,
    }) => {
        if (!hasCadastroId || savingEmissionRef.current || savingValidationRef.current) return

        savingEmissionRef.current = true
        setIsSavingEmission(true)

        const requestVersion = requestVersionRef.current
        const savedRevision = snapshotRevisionRef.current

        const isCurrent = () => (
            mountedRef.current
            && requestVersionRef.current === requestVersion
            && snapshotRevisionRef.current === savedRevision
        )

        try {
            const response = await motorService.updateCadastroCaracteristicas(
                cadastroId,
                {
                    SpreadEmissao: spreadEmissao,
                    PrazoMedioEmissao: prazoMedioEmissao,
                }
            )

            if (!isCurrent() || !response) return

            dispatchSnackbar(
                "Spread e prazo da emissão atualizados com sucesso.",
                "success"
            )

            onEmissionSaved?.({
                SpreadEmissao: response.body.SpreadEmissao,
                PrazoMedioEmissao: response.body.PrazoMedioEmissao,
            })

            return response.body
        } catch (error) {
            if (!isCurrent()) return

            dispatchSnackbar(
                getSaveErrorMessage(error, "Não foi possível atualizar o spread e o prazo da emissão."),
                "error"
            )
        } finally {
            savingEmissionRef.current = false

            if (mountedRef.current) {
                setIsSavingEmission(false)
            }
        }
    }, [
        cadastroId,
        hasCadastroId,
        motorService,
        onEmissionSaved,
    ])

    const saveValidation = useCallback(async (validationPayload) => {
        if (!hasCadastroId || savingValidationRef.current || savingEmissionRef.current) return

        savingValidationRef.current = true
        setIsSavingValidation(true)

        const requestVersion = requestVersionRef.current
        const savedRevision = snapshotRevisionRef.current

        const isCurrent = () => (
            mountedRef.current
            && requestVersionRef.current === requestVersion
            && snapshotRevisionRef.current === savedRevision
        )

        try {
            const payload = buildValidationPayload(validationPayload)

            const response = await validacaoService.postValidacao(
                cadastroId,
                payload
            )

            if (!isCurrent() || !response) return

            setLookup({
                key: requestKey,
                snapshotRevision: savedRevision,
                loading: false,
                viewModel: mapSavedValidation(response.body).viewModel,
            })
            dispatchSnackbar(
                "Validação salva com sucesso.",
                "success"
            )

            return response.body
        } catch (error) {
            if (!isCurrent()) return

            dispatchSnackbar(
                getSaveErrorMessage(error, "Não foi possível salvar a validação."),
                "error"
            )
        } finally {
            savingValidationRef.current = false

            if (mountedRef.current) {
                setIsSavingValidation(false)
            }
        }
    }, [
        cadastroId,
        hasCadastroId,
        validacaoService,
        requestKey,
    ])

    const exportExcel = useCallback(async (payload, memoryColumns) => {
        return validacaoService.exportValidationExcel(payload, memoryColumns)
    }, [validacaoService])

    return {
        parameters,
        setParameters: updateParameters,
        savedViewModel: currentLookup.viewModel,
        isLoadingValidation: currentLookup.loading,
        referenceDateError,
        isSavingEmission,
        saveEmission,
        isSavingValidation,
        saveValidation,
        exportExcel,
    }
}

export default useValidationData
